import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { Facebook, Instagram } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { uniq } from 'lodash'
import React, { memo } from 'react'

export interface Props {
  counter?: string
  facebookURL?: string
  images?: ImageProps[]
  instagramURL?: string
  instagramUsername?: string
  languageCode: string
  title?: string
}

export const SocialWall = memo(function SocialWall({
  counter,
  facebookURL,
  images,
  instagramURL,
  instagramUsername,
  languageCode,
  title,
}: Props) {
  return (
    <Container>
      <Head dial={7} wrap row space="between">
        <FadeInUp delay={0.4}>
          <LabelCounter
            className="item-label"
            variant="light"
            label={useVocabularyData('social', languageCode)}
            counter={counter}
          />
        </FadeInUp>
        {title ? <Title tag="h3" text={title} /> : null}

        {facebookURL || instagramURL ? (
          <SocialWrap row>
            {facebookURL ? (
              <FadeInUp delay={0.6}>
                <Anchor
                  aria-label="Facebook"
                  href={facebookURL}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Facebook />
                </Anchor>
              </FadeInUp>
            ) : null}
            {instagramURL ? (
              <FadeInUp delay={0.8}>
                <Anchor
                  aria-label="Instagram"
                  href={instagramURL}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Instagram />
                </Anchor>
              </FadeInUp>
            ) : null}
          </SocialWrap>
        ) : null}
      </Head>
      {images ? (
        <Wrapper dial={5} row>
          {uniq(images)
            .slice(0, 4)
            .map((item, index) => (
              <DropDown delay={index * 0.2} key={index}>
                <Item>
                  <Image {...item} />
                </Item>
              </DropDown>
            ))}
        </Wrapper>
      ) : null}
      {instagramUsername && instagramURL ? (
        <Anchor
          aria-label="Instagram"
          href={instagramURL}
          rel="noreferrer"
          target="_blank"
        >
          {instagramUsername}
        </Anchor>
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  padding: 6rem 10vw 4.5rem;
  position: relative;

  &:before {
    content: '';
    width: 80.2085vw;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.primaryDark};
  }

  @media (max-width: 1199px) {
    padding: 3rem 1.5rem 0 1.5rem;

    &::before {
      width: 100%;
      bottom: 4rem;
      height: auto;
    }
  }
`

const Head = styled(FlexBox)`
  width: 70.2085vw;

  > div:first-of-type {
    width: 100%;
  }

  @media (max-width: 1199px) {
    width: 100%;
    align-items: center;
    flex-direction: column;
    text-align: center;

    .item-label {
      justify-content: center;
    }
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xl}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
`

const SocialWrap = styled(FlexBox)`
  margin-right: 5.556vw;
  margin-bottom: 0.875rem;

  > div:first-of-type {
    margin-right: 0.75rem;
  }

  a {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    border: 0.125rem solid ${rgba(theme.colors.variants.neutralLight3, 0.1)};
    transition: all 0.3s ease-in-out;

    svg {
      fill: ${theme.colors.variants.neutralLight3};
      height: 1rem;
    }

    &:hover {
      border: 0.125rem solid ${rgba(theme.colors.variants.neutralLight3, 1)};
    }
  }

  @media (max-width: 1199px) {
    margin: 1.125rem 0 0 0;
  }
`

const Anchor = styled.a`
  ${styles.description.light}

  display: inline-block;
  text-align: left;
  transition: opacity 0.3s;
  position: relative;
  z-index: 1;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Wrapper = styled(FlexBox)`
  padding: 2.25rem 0;
  gap: 4.028vw;

  > div {
    aspect-ratio: 1;
    width: 19.583vw;
    position: relative;

    img {
      ${styles.img}
    }
  }

  @media (max-width: 1199px) {
    padding: 1.5rem 0 0 0;
    flex-wrap: wrap;
    gap: 0.75rem;

    > div {
      width: calc(50% - 0.375rem);
    }
  }
`
const Item = styled.div``
